// ─────────────────────────────────────────────────────────────────────────────
// Users page — the "User Directory". This component only wires things together:
//     • useUserFilters — search / status / role / team + current page
//     • useUsersData   — fetches the paged list + exposes row mutations
//     • local state    — whether the invite modal is open
//   All rendering lives in ./components.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from "react";
import styles from "./Users.module.css";
import { usePermissions, MODULES } from "../../features/auth/model/usePermissions";
import { useUserFilters } from "./hooks/useUserFilters";
import { useUsersData } from "./hooks/useUsersData";
import DirectoryCard from "./components/DirectoryCard";
import InviteModal from "./components/InviteModal";
import UserDetailModal from "./components/UserDetailModal";

export default function Users() {
  // Read-Write (or Full) on User Management unlocks the write controls; a Read
  // role (e.g. IT Manager) sees the directory read-only.
  const { canWrite } = usePermissions();
  const canManageUsers = canWrite(MODULES.USER_MANAGEMENT);

  const {
    search, setSearch,
    filterStatus, setFilterStatus,
    filterRole, setFilterRole,
    filterTeam, setFilterTeam,
    page, setPage, hasFilters,
  } = useUserFilters();

  const {
    users, total, teams, loading,
    error, clearError,
    refetch, pageSize,
  } = useUsersData({ search, filterStatus, filterRole, filterTeam, page });

  const [showInvite, setShowInvite] = useState(false);
  // The row the detail modal is showing (null → closed).
  const [selectedUser, setSelectedUser] = useState(null);

  return (
    <div className={styles.page}>
      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onSuccess={() => refetch({ page: 1 })}
        />
      )}

      {selectedUser && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
        />
      )}

      {/* ── Action error banner (outside the shell, above it) ── */}
      {error && (
        <div className={styles.errorBanner}>
          ⚠ {error}
          <button onClick={clearError}>✕</button>
        </div>
      )}

      {/* ── Single glass shell: header + toolbar + table + footer ── */}
      <div className={styles.pageShell}>
        <div className={styles.pageHeader}>
          <div className={styles.pageTitle}>
            <div className={styles.pageTitleIcon}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.67">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
              </svg>
            </div>
            <div className={styles.pageTitleText}>
              <h2 className={styles.pageName}>User Directory</h2>
              <p className={styles.pageSub}>Manage access, roles, and provisioning</p>
            </div>
          </div>
          {canManageUsers && (
            <button className={styles.inviteBtn} onClick={() => setShowInvite(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
              </svg>
              Invite User
            </button>
          )}
        </div>

        <DirectoryCard
          search={search} onSearch={setSearch}
          filterStatus={filterStatus} onFilterStatus={setFilterStatus}
          filterRole={filterRole} onFilterRole={setFilterRole}
          filterTeam={filterTeam} onFilterTeam={setFilterTeam}
          teams={teams} hasFilters={hasFilters}
          users={users} loading={loading} total={total}
          page={page} onPage={setPage} pageSize={pageSize}
          onRowClick={setSelectedUser}
        />
      </div>
    </div>
  );
}
